import React, { useState, useRef } from 'react';
import Image from 'next/legacy/image'; // Import Image from Next.js
import DrawerButton from '../Button/DrawerButton';
import ReviewCard from '../Review/ReviewCard';
import { BsCheck2Circle } from 'react-icons/bs';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { StaticImageData } from 'next/image';

// Define the TopCardInstaller component
interface Review {
  name: string;
  content: string;
  rating: string;
  date: string;
  location: string;
}

interface InstallerData {
  name: string;
  imageSrc: StaticImageData;
  rating: string;
  reasonsWhy: string[];
  installerStrengths: string[];
  concerns: string[];
  reviews: Review[];
  headquarters: string;
  serviceArea: string;
}

const TopCardInstaller: React.FC<{ installerData: InstallerData }> = ({
  installerData,
}) => {
  const {
    name,
    imageSrc,
    rating,
    reasonsWhy,
    installerStrengths,
    concerns,
    reviews,
    headquarters,
    serviceArea,
  } = installerData;
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const ToggleBar = () => (
    <div
      className="flex justify-center items-center bg-gray-200 rounded-b-md p-2 cursor-pointer"
      onClick={() => setIsOpen(!isOpen)}
      style={{ transition: 'all 0.3s ease-in-out' }}
    >
      {isOpen ? <FaChevronUp size={20} /> : <FaChevronDown size={20} />}
    </div>
  );

  return (
    <div
      ref={containerRef}
      className="flex flex-col border-2 border-primary shadow-lg rounded-3xl mb-8 max-h-[80vh] overflow-y-auto relative"
    >
      <div className="sticky top-0 z-10 bg-white shadow-md">
        <div className="flex flex-col md:flex-row items-center justify-between py-4 px-8 border-b border-gray-100">
          <div className="md:w-1/2 flex items-center justify-start">
            <div className="w-48">
              <Image
                src={imageSrc}
                alt={name}
                className="object-contain"
                width={300}
                height={60}
                priority
              />
            </div>
            <div className="ml-4 hidden md:flex items-center text-primary-500">
              <BsCheck2Circle className="text-xl mr-2" />
              <p className="font-semibold text-sm">Recommended</p>
            </div>
          </div>

          <div className="md:w-auto flex flex-col md:flex-row items-center gap-3 mt-3 md:mt-0">
            <div className="flex md:hidden items-center text-primary-500 mb-2">
              <BsCheck2Circle className="text-xl mr-2" />
              <p className="font-semibold text-sm">Recommended</p>
            </div>
            <DrawerButton size="sm">Compare options</DrawerButton>
          </div>
        </div>
      </div>
      <div className="px-4 md:px-12 pb-6 pt-4">
        <h3 className="font-bold text-xl">
          Your Installer is <span className="underline">{name}</span>.
        </h3>
        <div></div>
        <p className="font-medium text-lg py-2 text-black">
          Why EnergyPal Recommends:
        </p>
        <ol className="list-decimal pl-4 ml-4 space-y-1">
          {reasonsWhy.map((reason, index) => (
            <li key={index}>{reason}</li>
          ))}
        </ol>
        <p className="font-medium text-lg py-2 text-black">
          Installer Strengths:
        </p>
        <ul className="list-none pl-4 space-y-1">
          {installerStrengths.map((strength, index) => (
            <li
              key={index}
              className="mb-2"
              style={{ listStyle: 'inside', listStyleType: 'disc' }}
            >
              {strength}
            </li>
          ))}
        </ul>
        <p className="font-medium text-lg py-2 text-black">Concerns:</p>
        <ul className="list-none pl-4">
          {concerns.map((concern, index) => (
            <li
              key={index}
              className="mb-2"
              style={{ listStyle: 'inside', listStyleType: 'disc' }}
            >
              {concern}
            </li>
          ))}
        </ul>

        <div>
          <p className="font-medium text-lg py-2 text-black">Rating:</p>
          <p className="text-lg">
            {rating} <span className="text-yellow-400">★ ★ ★ ★ ★</span>
          </p>
        </div>

        {reviews.length > 0 && (
          <div>
            <p className="font-medium text-lg py-2 text-black">Reviews:</p>
            <div
              className={`reviews-container ${
                isOpen ? 'expanded' : 'collapsed'
              }`}
              style={{
                transition: 'all 0.3s ease-in-out',
                overflow: 'hidden',
                maxHeight: isOpen ? 'none' : '80px',
                position: 'relative',
              }}
            >
              {reviews.map((review, index) => (
                <div key={index} className="my-2">
                  <ReviewCard
                    key={index}
                    name={review.name}
                    content={review.content}
                    rating={review.rating}
                    date={review.date}
                    location={review.location}
                    company={name}
                  />
                </div>
              ))}
            </div>

            {!isOpen && <ToggleBar />}
            {isOpen && <ToggleBar />}

            <style jsx>{`
              .reviews-container.collapsed::after {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: linear-gradient(to bottom, transparent, white 80%);
                pointer-events: none;
              }
              .reviews-container.expanded::after {
                content: none;
              }
            `}</style>
          </div>
        )}

        <p className="font-medium text-lg py-2 text-black">Headquarters:</p>
        <p>{headquarters}</p>
        <p className="font-medium text-lg py-2 text-black">Service Area:</p>
        <p>{serviceArea}</p>
      </div>
    </div>
  );
};

export default TopCardInstaller;
